// fleet-state.ts — real reads of fleet state when running inside Tauri.
// Web/PWA mode falls back to a doctrine-baseline shape so the UI still renders.

import { invoke } from "@tauri-apps/api/core";

export interface FleetState {
  host: string;
  timestamp: string;
  drift_detected: boolean;
  reconciled: boolean;
  local_counts: { item_count: number; skills: number; agents: number; commands: number; scripts: number };
  peer_reachability: Record<string, boolean>;
  peer_counts: Array<{ host: string; item_count: number }>;
  last_sync_mode?: string;
}

function isTauri(): boolean {
  return typeof window !== "undefined" && Boolean((window as any).__TAURI_INTERNALS__);
}

export async function getFleetState(): Promise<FleetState | null> {
  if (isTauri()) {
    try {
      // Rust command get_fleet_state reads ~/.claude/.fleet-sync-state.json
      // (BOM-stripped, JSON-parsed). Shape matches the writer in scripts/fleet-sync.ps1.
      const result = await invoke<any>("get_fleet_state");
      // The real file writes peer_counts as an OBJECT (often {}), not an array —
      // v0.1.0 typed it as an array and crashed React on launch (blank window).
      const rawPeers = result.peer_counts;
      const peer_counts: Array<{ host: string; item_count: number }> = Array.isArray(rawPeers)
        ? rawPeers
        : rawPeers && typeof rawPeers === "object"
          ? Object.entries(rawPeers).map(([host, v]: [string, any]) => ({
              host,
              item_count: typeof v === "number" ? v : (v?.item_count ?? 0),
            }))
          : [];
      return {
        host: result.host,
        timestamp: result.timestamp,
        drift_detected: result.drift_detected === true,
        reconciled: result.reconciled === true,
        local_counts: result.local_counts ?? { item_count: 0, skills: 0, agents: 0, commands: 0, scripts: 0 },
        peer_reachability: result.peer_reachability ?? {},
        peer_counts,
        last_sync_mode: result.last_sync_mode,
      };
    } catch (err: any) {
      console.warn("[fleet-state] Tauri invoke failed:", err?.message ?? err);
      // Fall through to web baseline so the UI doesn't crash on first run.
    }
  }
  // Web fallback (no .fleet-sync-state.json access from a browser).
  // Shows a doctrine-baseline so the UI renders; real data only on the desktop app.
  return {
    host: "web-preview",
    timestamp: new Date().toISOString(),
    drift_detected: false,
    reconciled: true,
    local_counts: { item_count: 0, skills: 0, agents: 0, commands: 0, scripts: 0 },
    peer_reachability: { MILLSTONE: false, SAGENODE: false, HP_ENVY: false },
    peer_counts: [],
    last_sync_mode: "web-preview",
  };
}

export interface DeviceRecord {
  name: string;
  ip?: string;
  role: string;
  archetype?: string;
  reachable: boolean;
  category: "primary" | "sidecar" | "stationary";
}

export async function getDevices(): Promise<DeviceRecord[]> {
  let manifestPaths: Record<string, any> = {};
  if (isTauri()) {
    try {
      manifestPaths = await invoke<Record<string, any>>("get_devices");
    } catch (err: any) {
      console.warn("[fleet-state] get_devices failed:", err?.message ?? err);
    }
  }
  // Fold manifest data into the device records. Category is derived from doctrine.
  const categoryFor = (name: string): DeviceRecord["category"] => {
    if (name === "KYRA-THINKPAD" || name === "S25_ULTRA" || name === "GALAXY_XR") return "primary";
    if (name === "MILLSTONE" || name === "SAGENODE" || name === "HP_ENVY") return "stationary";
    return "sidecar";
  };
  // Static doctrine list, augmented with manifest values when available.
  const baseList: DeviceRecord[] = [
    { name: "KYRA-THINKPAD", ip: "192.168.8.105", role: "Cartographer (mobile primary)",          archetype: "KyraAI",   reachable: true,  category: "primary" },
    { name: "MILLSTONE",     ip: "192.168.8.180", role: "Orchestrator (canonical .claude/)",      archetype: "GeorgeAI", reachable: true,  category: "stationary" },
    { name: "SAGENODE",      ip: "192.168.8.100", role: "Sovereign of Capital (Docker / DCE)",    archetype: "SageAI",   reachable: true,  category: "stationary" },
    { name: "HP_ENVY",       ip: "192.168.8.103", role: "Tertiary (queued-for-setup)",            archetype: "Rowan",    reachable: false, category: "stationary" },
    { name: "S25_ULTRA",     ip: "192.168.8.184", role: "Field companion",                        archetype: "Lucie",    reachable: false, category: "primary" },
    { name: "GALAXY_XR",                          role: "Spatial overlay",                        archetype: "Aria",     reachable: false, category: "primary" },
    { name: "iPhone 15",                          role: "Field accessory",                                                reachable: false, category: "sidecar" },
    { name: "Note 9",                             role: "Legacy sidecar",                                                  reachable: false, category: "sidecar" },
    { name: "OMI pendant",                        role: "Wearable capture (incoming)",                                     reachable: false, category: "sidecar" },
  ];
  // Surface manifest status (e.g. queued-for-setup) when present
  for (const d of baseList) {
    const meta = manifestPaths[d.name];
    if (meta && typeof meta === "object") {
      if (meta.status === "queued-for-setup") {
        d.reachable = false;
        d.role = d.role + " · " + meta.status;
      }
    }
    d.category = categoryFor(d.name);
  }
  return baseList;
}
